/**
 * What a change of session does to this tab's cached data and route guards. Supabase broadcasts
 * every auth event to all of a browser's tabs, so this runs in each of them at the same moment.
 */

import type { QueryClient } from "@tanstack/react-query";
import type { AnyRouter } from "@tanstack/react-router";
import { getSessionSnapshot, subscribeSession } from "@lato/client/lib/supabase";

type SessionSnapshot = ReturnType<typeof getSessionSnapshot>;

function sessionUserId(session: SessionSnapshot): string | null {
  return session?.user.id ?? null;
}

/** The acting org the token carries. The backend treats this claim as the request's org. */
export function tokenOrgId(session: SessionSnapshot): string | null {
  try {
    const payload = session!.access_token.split(".")[1]!.replace(/-/g, "+").replace(/_/g, "/");
    return (JSON.parse(atob(payload)) as { org_id?: string }).org_id ?? null;
  } catch {
    return null;
  }
}

/**
 * Re-run route guards when auth state changes (sign-in or sign-out). When the signed-in identity
 * itself changes, drop every cached query: what a viewer may see (staff vs. customer, one org vs.
 * another) is decided per principal, so one identity's data must never render for the next.
 *
 * A background tab's guards wait until it is shown. Guards fetch, and a sign-in landing in every
 * tab at once was a page load per tab. The cache is still dropped at once, so nothing outlives its
 * viewer. A token for another org, from a switch in another tab, marks data stale without fetching:
 * the tab that switched refetches for itself, and any other when something next reads the data.
 */
export function watchSession(router: AnyRouter, client: QueryClient): () => void {
  let previous = getSessionSnapshot();
  let guardsOutOfDate = false;

  const rerunGuards = () => {
    guardsOutOfDate = document.visibilityState !== "visible";
    if (!guardsOutOfDate) void router.invalidate();
  };
  const onVisibilityChange = () => {
    if (guardsOutOfDate) rerunGuards();
  };

  document.addEventListener("visibilitychange", onVisibilityChange);
  const unsubscribe = subscribeSession(() => {
    const session = getSessionSnapshot();
    if (sessionUserId(previous) !== sessionUserId(session)) client.clear();
    else if (tokenOrgId(previous) !== tokenOrgId(session)) {
      void client.invalidateQueries({ refetchType: "none" });
    }
    if (!!previous !== !!session) rerunGuards();
    previous = session;
  });
  return () => {
    unsubscribe();
    document.removeEventListener("visibilitychange", onVisibilityChange);
  };
}
